import Bluebird from 'bluebird';
import moment from 'moment';
import { strTrimSpaces } from '../../../../../services/strings';
import { AjaxError } from '../../../services/ajax';
import { apiCall } from '../../../services/api';
import { pageLang } from '../../../services/app-storage';
import InputField from '../../../services/input-fields/input-field';
import getErrorMessages from '../errors/error-storage';
import { removeError, setError, updateErrorView } from '../errors/errors';
import { storeFlight } from '../quote-data';
// tslint:disable-next-line:max-line-length
import { clearStopover, flightNumSelected, getSelectedFlightID, IStopOverFlight, showSelectableStopover, showStaticStopover } from '../stopover';
import { isDesktop } from '../storage';

export function flightIsValid(numberField: InputField, dateField: InputField): Bluebird<boolean> {

    // Read flight input
    const flightNumber = strTrimSpaces(numberField.value.trim());
    let flightDate = dateField.value;

    // Remove errors
    removeError(numberField);
    removeError(dateField);

    // Check input complete
    if (!flightNumber || !flightDate) {
        return updateErrorView().thenReturn(false);
    }

    // Format flight date
    if (isDesktop) {
        flightDate = moment(flightDate, 'll', pageLang)
            .format('YYYY-MM-DD') || flightDate;
    }

    // Call flights API
    return getFlights(flightNumber, flightDate).then(flights => {
        if (flights.length === 0) {

            // No flights retrieved
            return getErrorMessages()
                .then(errors => setError(numberField, errors.flightNumber.notFound))
                .thenReturn(false);

        } else if (flights.length > 1) {

            // Layover flight
            const isReturn = numberField.isReturn();

            if (flightNumSelected({
                flightnum: flightNumber,
                flightDatetime: flightDate
            }, isReturn)) {
                const flightID = getSelectedFlightID(isReturn);

                if (flightID === null) {
                    // No layover selected
                    return getErrorMessages()
                        .then(errors => setError(numberField, errors.flight.noStepOverSelected))
                        .thenReturn(false);
                }
            }
        }

        return true;
    }, (errorResponse: AjaxError) => getErrorMessages().then(errors => {
        const response = errorResponse.response as { message: string };
        if (!response || !response.message) {
            setError(numberField, errors.booking.failed);
            return false;
        }

        switch (response.message) {
            case 'The flight id is incorrectly formatted.':
                setError(numberField, errors.flightNumber.notFound);
                break;
            default:
                console.warn('Unknown flight number error message:', response.message);
                setError(numberField, errors.booking.failed);
                break;
        }

        return updateErrorView().thenReturn(false);
    })).then(isValid => updateErrorView().thenReturn(isValid));
}

function getFlights(flightNumber: string, flightDate: string): Bluebird<API.ApiFlightCheck[]> {
    flightNumber = encodeURIComponent(flightNumber);
    flightDate = encodeURIComponent(flightDate);

    return apiCall<API.FlightCheckResponse>('GET', `flights/layover/${flightNumber}/${flightDate}`, null, {
        groupURL: 'flights/layover/{flightNumber}/{flightDate}',
        ignoreClientErrors: true
    }).then(response => {
        const flights = response.data.flights;
        flights.forEach(flight => {
            storeFlight(flight.id, {
                number: flightNumber,
                departureMoment: flight.dep_datetime_local,
                departureZuluMoment: flight.dep_datetime_zulu,
                departureZuluMoment_str: flight.dep_datetime_zulu,
                departureAirport: flight.departure.name,
                arrivalMoment: flight.arr_datetime_local,
                arrivalZuluMoment: flight.arr_datetime_zulu,
                arrivalZuluMoment_str: flight.dep_datetime_zulu,
                arrivalAirport: flight.arrival.name
            });
        });
        return flights;
    });
}

export async function validateFlight(
    numberField: InputField,
    dateField: InputField,
    updateView: boolean): Bluebird<boolean> {

    const isValid = await flightIsValid(numberField, dateField);

    // TODO: Legacy code below

    const $flightNumber = numberField.$JQuery,
        $flightDate = dateField.$JQuery;

    // Read flight input
    const flightNumber = strTrimSpaces(numberField.value.trim());
    let flightDate = dateField.value;

    // Apply field colors
    $flightNumber.toggleClass('isGoed', isValid);
    $flightDate.toggleClass('isGoed', isValid);

    if (flightNumber && flightDate) {
        $flightNumber.toggleClass('isFout', !isValid);
        $flightDate.toggleClass('isFout', !isValid);
    } else if (updateView) {
        $flightNumber.toggleClass('isFout', !flightNumber);
        $flightDate.toggleClass('isFout', !flightDate);
    }

    if (!updateView) return isValid;

    // Format flight date
    if (isDesktop) {
        flightDate = moment(flightDate, 'll', pageLang)
            .format('YYYY-MM-DD') || flightDate;
    }

    // Handle layover windows
    const isReturn = numberField.isReturn();
    if (!isValid) {
        clearStopover(isReturn);
        return isValid;
    }

    const flightInput = {
        flightnum: flightNumber,
        flightDatetime: flightDate
    };

    if (!flightNumSelected(flightInput, isReturn)) {
        const flights = await getFlights(flightNumber, flightDate);
        if (flights.length === 1) {
            showStaticStopover(flightInput, getStopOverFlight(flights[0]), isReturn);
        } else if (flights.length > 1) {
            // Flight has a layover
            const stopOverFlights: IStopOverFlight[] = [];
            flights.forEach((flight: API.ApiFlightCheck) => {
                stopOverFlights.push(getStopOverFlight(flight));
            });
            showSelectableStopover(flightInput, stopOverFlights, isReturn);
        }
    }

    return isValid;
}

function getStopOverFlight(apiFlight: API.ApiFlightCheck): IStopOverFlight {
    return {
        id: apiFlight.id,
        datetime: moment(apiFlight.dep_datetime_local, 'YYYY-MM-DD HH:mm:ss'),
        origin: {
            id: apiFlight.departure.id,
            iata: apiFlight.departure.iata,
            name: apiFlight.departure.name,
        },
        destination: {
            id: apiFlight.arrival.id,
            iata: apiFlight.arrival.iata,
            name: apiFlight.arrival.name,
        },
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/assets/scripts/modules/widget-core/validation/flight-validator.ts